import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * @typedef {object} PackLine
 * @property {string} key - Identificador único de la línea dentro del pack.
 * @property {string} productId
 * @property {string} url - URL de la ficha, para volver a abrirla.
 * @property {string} title
 * @property {string} variantId
 * @property {string} variantTitle - Vacío en productos sin opciones.
 * @property {number} price - En céntimos.
 * @property {string} image
 * @property {[string, string][]} properties - Propiedades de texto del grabado.
 * @property {[string, File][]} files - Archivos (el logo).
 * @property {number} [servicePrice] - Grabado como servicio aparte, en céntimos (ya incluido en price).
 */

/**
 * «Arma tu pack».
 *
 * Guarda lo que el cliente va eligiendo en cada paso, pinta el resumen y suma
 * todo al carrito. Un producto con opciones se configura en una ventana que
 * carga la ficha real del producto: el selector de variantes y el bloque de
 * grabado del tema funcionan ahí igual que en la ficha. Cuando el cliente
 * confirma, en vez de añadir al carrito se lee el formulario (variante y
 * propiedades del grabado) y la línea entra al pack.
 */
class MrPackBuilder extends HTMLElement {
  #controller = new AbortController();

  /** @type {Map<number, PackLine[]>} */
  #lines = new Map();

  #current = 0;
  #lineCount = 0;

  /** @type {{ step: number, card: HTMLElement, replaceKey?: string, confirming?: boolean } | null} */
  #pending = null;

  /** @type {AbortController | null} */
  #dialogController = null;

  /** @type {string} */
  #pageUrl = '';

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });

    // Esc cierra la ventana por su cuenta: se limpia en «cancel», que llega
    // en el momento, y en «close» por si acaso.
    this.#dialog?.addEventListener('cancel', this.#handleDialogClose, { signal });
    this.#dialog?.addEventListener('close', this.#handleDialogClose, { signal });
    this.#dialog?.addEventListener('click', this.#handleBackdropClick, { signal });

    this.#dialog?.style.setProperty('--mr-pack-confirm-label', JSON.stringify(this.dataset.confirmLabel ?? ''));

    this.#render();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#dialogController?.abort();
  }

  get #steps() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-mr-step]')]);
  }

  get #dialog() {
    const dialog = this.querySelector('[data-mr-dialog]');
    return dialog instanceof HTMLDialogElement ? dialog : null;
  }

  get #allLines() {
    return [...this.#lines.values()].flat();
  }

  /* ------------------------------------------------------------------ */
  /* Pasos                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * @param {number} index
   * @param {{ scroll?: boolean }} [options]
   */
  #goTo(index, { scroll = true } = {}) {
    const steps = this.#steps;
    if (!steps.length) return;

    this.#current = Math.max(0, Math.min(index, steps.length - 1));

    steps.forEach((step, i) => {
      step.hidden = i !== this.#current;
    });

    for (const tab of this.querySelectorAll('[data-mr-tab]')) {
      if (Number(tab.getAttribute('data-mr-tab')) === this.#current) {
        tab.setAttribute('aria-current', 'step');
      } else {
        tab.removeAttribute('aria-current');
      }
    }

    if (scroll) {
      const target = this.querySelector('.mr-pack__nav') ?? steps[this.#current];
      const top = target?.getBoundingClientRect().top ?? 0;
      if (top < 0 || top > window.innerHeight * 0.6) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    }
  }

  #advance() {
    if (this.#current < this.#steps.length - 1) this.#goTo(this.#current + 1);
  }

  /* ------------------------------------------------------------------ */
  /* Elegir productos                                                   */
  /* ------------------------------------------------------------------ */

  /** @param {HTMLElement} card */
  #pick(card) {
    const step = Number(card.closest('[data-mr-step]')?.getAttribute('data-mr-step'));
    if (Number.isNaN(step)) return;

    const multiple = this.#isMultiple(step);
    const existing = (this.#lines.get(step) ?? []).filter((line) => line.productId === card.dataset.productId);

    // En un paso de varios, volver a tocar un producto sin opciones lo quita.
    if (multiple && existing.length && card.dataset.hasOptions !== 'true') {
      this.#remove(step, existing[0].key);
      return;
    }

    if (card.dataset.hasOptions === 'true') {
      this.#openOptions(step, card);
      return;
    }

    this.#add(step, {
      key: this.#nextKey(),
      productId: card.dataset.productId ?? '',
      url: card.dataset.url ?? '',
      title: card.dataset.title ?? '',
      variantId: card.dataset.variantId ?? '',
      variantTitle: '',
      price: Number(card.dataset.price) || 0,
      image: card.dataset.image ?? '',
      properties: [],
      files: [],
    });
  }

  /** @param {number} step */
  #isMultiple(step) {
    return this.#steps[step]?.dataset.multiple === 'true';
  }

  #nextKey() {
    this.#lineCount += 1;
    return `l${this.#lineCount}`;
  }

  /**
   * @param {number} step
   * @param {PackLine} line
   * @param {string} [replaceKey] - Línea que se reemplaza (al «Cambiar»).
   */
  #add(step, line, replaceKey) {
    const current = this.#lines.get(step) ?? [];
    let next;

    if (replaceKey) {
      next = current.map((item) => (item.key === replaceKey ? line : item));
    } else if (this.#isMultiple(step)) {
      next = [...current, line];
    } else {
      next = [line];
    }

    this.#lines.set(step, next);
    this.#setStatus('');
    this.#render();

    if (!this.#isMultiple(step) && !replaceKey) this.#advance();
  }

  /**
   * @param {number} step
   * @param {string} key
   */
  #remove(step, key) {
    const next = (this.#lines.get(step) ?? []).filter((line) => line.key !== key);
    if (next.length) {
      this.#lines.set(step, next);
    } else {
      this.#lines.delete(step);
    }
    this.#render();
  }

  /* ------------------------------------------------------------------ */
  /* Ventana con la ficha del producto                                  */
  /* ------------------------------------------------------------------ */

  /**
   * @param {number} step
   * @param {HTMLElement} card
   * @param {string} [replaceKey]
   */
  async #openOptions(step, card, replaceKey) {
    const dialog = this.#dialog;
    const body = dialog?.querySelector('[data-mr-dialog-body]');
    const title = dialog?.querySelector('[data-mr-dialog-title]');
    if (!dialog || !body) return;

    this.#pending = { step, card, replaceKey };
    this.#pageUrl = window.location.href;

    if (title) title.textContent = card.dataset.title ?? '';
    body.replaceChildren(this.#loadingNode(this.querySelector('[data-mr-loading]')?.textContent ?? ''));
    if (!dialog.open) dialog.showModal();

    try {
      const response = await fetch(card.dataset.url ?? '', { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const section = html.querySelector('variant-picker, product-form-component')?.closest('.shopify-section');
      if (!section) throw new Error('No product section');

      // La ventana se cerró mientras cargaba, o se abrió otra.
      if (!dialog.open || this.#pending?.card !== card) return;

      const content = document.importNode(section, true);
      this.#trimProduct(content);
      body.replaceChildren(content);

      // Mientras cargan los módulos de la ficha, el botón no hace nada: si
      // llegara al tema, añadiría el producto al carrito por fuera del pack.
      this.#holdConfirm(content);
      await this.#loadScripts(section);
      if (this.#pending?.card === card) this.#watchConfirm(content);
    } catch (error) {
      console.error(error);
      if (dialog.open && this.#pending?.card === card) {
        body.replaceChildren(this.#loadingNode(this.dataset.loadErrorText ?? ''));
      }
    }
  }

  /** @param {string} text */
  #loadingNode(text) {
    const node = document.createElement('p');
    node.className = 'mr-pack__loading';
    node.textContent = text;
    return node;
  }

  /**
   * Deja en la ficha solo lo necesario para elegir: fotos, título, precio,
   * opciones, grabado y el botón. Lo demás (regalo, envío, descripciones,
   * acordeones, recomendaciones, la cantidad) se oculta.
   *
   * @param {Element} root
   */
  #trimProduct(root) {
    const hide = (/** @type {Element} */ element) => element.setAttribute('data-mr-pack-hidden', '');

    // El selector no debe tocar la URL de esta página al cambiar de variante.
    for (const picker of root.querySelectorAll('variant-picker')) {
      picker.setAttribute('data-template-product-match', 'false');
    }

    for (const element of root.querySelectorAll(
      'mr-gift-wrap, mr-shipping-bar, mr-upsell, mr-delivery, sticky-add-to-cart, .shopify-payment-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart, quantity-selector-component, .quantity-selector'
    )) {
      hide(element);
    }

    const picker = root.querySelector('variant-picker');
    const form = root.querySelector('product-form-component');
    const anchor = picker ?? form;
    if (!anchor) return;

    // Contenedor de los bloques de la columna de detalles: el antepasado común
    // del selector y del botón. Sus hijos que no contienen nada útil se ocultan.
    let details = anchor.parentElement;
    while (details && form && !details.contains(form)) details = details.parentElement;
    if (!details) return;

    const keep = 'variant-picker, mr-engraving, product-form-component, h1, product-price, [class*="price"]';
    for (const child of details.children) {
      if (child.matches(keep) || child.querySelector(keep)) continue;
      if (child.matches('script, style, link')) continue;
      hide(child);
    }

    // Fuera de la columna de detalles queda la galería; lo demás de la sección
    // (desplegables, textos) se oculta subiendo de nivel hasta la raíz.
    const keepOuter = 'media-gallery, slideshow-component, [class*="media-gallery"], img, video';
    let node = details;
    while (node && node !== root && node.parentElement) {
      const parent = node.parentElement;
      for (const sibling of parent.children) {
        if (sibling === node || sibling.matches('script, style, link')) continue;
        if (sibling.matches(keepOuter) || sibling.querySelector(keepOuter)) continue;
        hide(sibling);
      }
      node = parent;
    }
  }

  /**
   * Los <script> que trae el HTML no se ejecutan al insertarlo. Los módulos de
   * la ficha (grabado, simulación, precio) se importan aquí; el navegador no
   * los repite si la página ya los cargó.
   *
   * @param {Element} section
   */
  async #loadScripts(section) {
    const sources = new Set();
    for (const script of section.querySelectorAll('script[type="module"][src]')) {
      const src = script.getAttribute('src');
      if (src) sources.add(new URL(src, window.location.href).href);
    }

    // import() se resuelve cuando el módulo ya se ejecutó: sus elementos están
    // definidos y los de la ventana, conectados.
    await Promise.all([...sources].map((src) => import(src).catch((error) => console.error(error))));
  }

  /** @param {Element} root */
  #holdConfirm(root) {
    this.#dialogController?.abort();
    this.#dialogController = new AbortController();
    const { signal } = this.#dialogController;

    const hold = (/** @type {Event} */ event) => {
      if (!(event.target instanceof Element) || !root.contains(event.target)) return;
      if (event.type === 'click' && !event.target.closest('[ref="addToCartButton"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    };

    window.addEventListener('click', hold, { capture: true, signal });
    window.addEventListener('submit', hold, { capture: true, signal });
  }

  /**
   * Intercepta el botón de la ficha. Se registra en window, en captura, y
   * después de que el bloque de grabado registrara los suyos: así el grabado
   * valida primero (y frena el envío si falta algo) y solo si todo está bien
   * llega aquí, antes de que el tema lo añada al carrito.
   *
   * @param {Element} root
   */
  #watchConfirm(root) {
    this.#dialogController?.abort();
    this.#dialogController = new AbortController();
    const { signal } = this.#dialogController;

    const confirm = (/** @type {Event} */ event, /** @type {HTMLFormElement | null} */ form) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (form) this.#confirm(form);
    };

    window.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        const button = event.target.closest('[ref="addToCartButton"]');
        if (!(button instanceof HTMLButtonElement) || !root.contains(button)) return;
        if (button.disabled) return;

        confirm(event, button.form ?? root.querySelector('form[data-type="add-to-cart-form"], product-form-component form'));
      },
      { capture: true, signal }
    );

    window.addEventListener(
      'submit',
      (event) => {
        if (!(event.target instanceof HTMLFormElement) || !root.contains(event.target)) return;
        confirm(event, event.target);
      },
      { capture: true, signal }
    );
  }

  /** @param {HTMLFormElement} form */
  async #confirm(form) {
    const pending = this.#pending;
    if (!pending) return;

    const data = new FormData(form);
    const variantId = String(data.get('id') ?? '');
    if (!variantId) return;

    /** @type {[string, string][]} */
    const properties = [];
    /** @type {[string, File][]} */
    const files = [];

    for (const [name, value] of data.entries()) {
      const match = /^properties\[(.+)\]$/.exec(name);
      if (!match) continue;
      if (value instanceof File) {
        if (value.size) files.push([match[1], value]);
      } else if (value.trim()) {
        properties.push([match[1], value]);
      }
    }

    // Un segundo toque mientras se lee el precio no vuelve a sumar la línea.
    if (pending.confirming) return;
    pending.confirming = true;

    const { card } = pending;
    const variant = await this.#variantInfo(card.dataset.url ?? '', variantId);

    // Grabado como servicio aparte (bloque de grabado en modo servicio): la
    // variante no lo incluye. Su precio se suma a la línea; al carrito viaja
    // como _mr_servicio y assets/mr-engraving-service.js añade la línea anidada.
    const engraving = this.#dialog?.querySelector('mr-engraving[data-mr-service="true"]');
    const hasService = properties.some(([key]) => key === '_mr_servicio');
    const servicePrice =
      hasService && engraving instanceof HTMLElement ? Number(engraving.dataset.mrServicePrice) || 0 : 0;

    this.#add(
      pending.step,
      {
        key: this.#nextKey(),
        productId: card.dataset.productId ?? '',
        url: card.dataset.url ?? '',
        title: card.dataset.title ?? '',
        variantId,
        variantTitle: variant?.title && variant.title !== 'Default Title' ? variant.title : '',
        price: (variant?.price ?? (Number(card.dataset.price) || 0)) + servicePrice,
        image: variant?.image || card.dataset.image || '',
        properties,
        files,
        servicePrice,
      },
      pending.replaceKey
    );

    // Solo se cierra si la ventana sigue mostrando este producto.
    if (this.#pending === pending) this.#closeDialog();
  }

  /**
   * Precio, nombre y foto de la variante elegida, desde el JSON del producto.
   *
   * @param {string} url
   * @param {string} variantId
   * @returns {Promise<{ title: string, price: number, image: string } | null>}
   */
  async #variantInfo(url, variantId) {
    try {
      const product = await fetch(`${url.split('?')[0]}.js`, { credentials: 'same-origin' }).then((res) => res.json());
      const variant = product?.variants?.find((/** @type {any} */ item) => String(item.id) === variantId);
      if (!variant) return null;

      const src = variant.featured_image?.src ?? '';
      return {
        title: variant.title ?? '',
        price: Number(variant.price) || 0,
        image: src ? this.#sized(src) : '',
      };
    } catch {
      return null;
    }
  }

  /** @param {string} src */
  #sized(src) {
    const url = new URL(src, window.location.href);
    url.searchParams.set('width', '160');
    url.searchParams.set('height', '160');
    url.searchParams.set('crop', 'center');
    return url.href;
  }

  #closeDialog() {
    this.#dialog?.close();
    this.#handleDialogClose();
  }

  /** @param {Event} [event] */
  #handleDialogClose = (event) => {
    // El aviso «close» llega en diferido: si entretanto se abrió otro
    // producto, es de la ventana anterior y no hay nada que limpiar.
    if (event?.type === 'close' && this.#dialog?.open) return;

    this.#dialogController?.abort();
    this.#dialogController = null;
    this.#pending = null;

    const body = this.#dialog?.querySelector('[data-mr-dialog-body]');
    body?.replaceChildren();

    // Por si el selector del tema llegó a escribir ?variant= en la dirección.
    if (this.#pageUrl && window.location.href !== this.#pageUrl) {
      history.replaceState(history.state, '', this.#pageUrl);
    }
  };

  /** @param {MouseEvent} event */
  #handleBackdropClick = (event) => {
    if (event.target === this.#dialog) this.#closeDialog();
  };

  /* ------------------------------------------------------------------ */
  /* Resumen                                                            */
  /* ------------------------------------------------------------------ */

  #render() {
    const lines = this.#allLines;
    const total = lines.reduce((sum, line) => sum + line.price, 0);

    this.#renderCards();
    this.#renderTabs();
    this.#renderLines();

    const totalNode = this.querySelector('[data-mr-total]');
    if (totalNode) totalNode.textContent = this.#money(total);

    const add = this.querySelector('[data-mr-add]');
    if (add instanceof HTMLButtonElement && add.getAttribute('aria-busy') !== 'true') add.disabled = !lines.length;

    this.#renderShipping(total);
    this.#renderBar(lines.length, total);
  }

  #renderCards() {
    for (const card of this.querySelectorAll('[data-mr-pick]')) {
      if (!(card instanceof HTMLElement)) continue;
      const step = Number(card.closest('[data-mr-step]')?.getAttribute('data-mr-step'));
      const chosen = (this.#lines.get(step) ?? []).some((line) => line.productId === card.dataset.productId);
      card.setAttribute('aria-pressed', String(chosen));

      const cta = card.querySelector('.mr-pack__card-cta');
      if (cta instanceof HTMLElement) {
        cta.dataset.label ??= cta.textContent ?? '';
        cta.textContent = chosen ? this.dataset.chosenLabel ?? '' : cta.dataset.label;
      }
    }
  }

  #renderTabs() {
    for (const tab of this.querySelectorAll('[data-mr-tab]')) {
      const step = Number(tab.getAttribute('data-mr-tab'));
      tab.setAttribute('data-done', String(Boolean(this.#lines.get(step)?.length)));
    }
  }

  #renderLines() {
    const list = this.querySelector('[data-mr-lines]');
    if (!list) return;

    const items = this.#steps.map((stepElement, step) => {
      const lines = this.#lines.get(step) ?? [];
      const stepTitle = stepElement.dataset.title ?? '';

      if (!lines.length) {
        const item = document.createElement('li');
        item.className = 'mr-pack__line mr-pack__line--empty';
        const name = document.createElement('span');
        name.className = 'mr-pack__line-step';
        name.textContent = stepTitle;
        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'mr-pack__line-action';
        go.dataset.mrGo = String(step);
        go.textContent = this.dataset.emptyLabel ?? '';
        item.append(name, go);
        return [item];
      }

      return lines.map((line) => this.#lineNode(step, stepTitle, line));
    });

    list.replaceChildren(...items.flat());
  }

  /**
   * @param {number} step
   * @param {string} stepTitle
   * @param {PackLine} line
   */
  #lineNode(step, stepTitle, line) {
    const item = document.createElement('li');
    item.className = 'mr-pack__line';

    const thumb = document.createElement(line.image ? 'img' : 'span');
    thumb.className = 'mr-pack__line-thumb';
    if (thumb instanceof HTMLImageElement) {
      thumb.src = line.image;
      thumb.alt = '';
      thumb.width = 48;
      thumb.height = 48;
      thumb.loading = 'lazy';
    }

    const name = document.createElement('span');
    name.className = 'mr-pack__line-name';

    const stepLabel = document.createElement('span');
    stepLabel.className = 'mr-pack__line-meta';
    stepLabel.textContent = stepTitle;

    const title = document.createElement('span');
    title.textContent = line.title;
    name.append(stepLabel, title);

    if (line.variantTitle) {
      const meta = document.createElement('span');
      meta.className = 'mr-pack__line-meta';
      meta.textContent = line.variantTitle;
      name.append(meta);
    }

    const engraving = [
      ...line.properties.filter(([key]) => !key.startsWith('_')).map(([key, value]) => `${key}: ${value}`),
      ...line.files.map(([key, file]) => `${key}: ${file.name}`),
    ];
    if (engraving.length) {
      const detail = document.createElement('span');
      detail.className = 'mr-pack__line-engraving';
      detail.textContent = `${this.dataset.engravedLabel ?? ''} · ${engraving.join(' · ')}`;
      name.append(detail);
    }

    const price = document.createElement('span');
    price.className = 'mr-pack__line-price';
    price.textContent = this.#money(line.price);

    const actions = document.createElement('span');
    actions.className = 'mr-pack__line-actions';

    const card = this.#cardFor(step, line.productId);
    if (card?.dataset.hasOptions === 'true') {
      const change = document.createElement('button');
      change.type = 'button';
      change.className = 'mr-pack__line-action';
      change.dataset.mrChange = line.key;
      change.dataset.step = String(step);
      change.textContent = this.dataset.changeLabel ?? '';
      actions.append(change);
    }

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'mr-pack__line-action';
    remove.dataset.mrRemove = line.key;
    remove.dataset.step = String(step);
    remove.textContent = this.dataset.removeLabel ?? '';
    remove.setAttribute('aria-label', `${this.dataset.removeLabel ?? ''}: ${line.title}`);
    actions.append(remove);

    item.append(thumb, name, price, actions);
    return item;
  }

  /**
   * @param {number} step
   * @param {string} productId
   * @returns {HTMLElement | null}
   */
  #cardFor(step, productId) {
    const card = this.#steps[step]?.querySelector(`[data-mr-pick][data-product-id="${CSS.escape(productId)}"]`);
    return card instanceof HTMLElement ? card : null;
  }

  /**
   * Barra de metas (envío gratis y descuentos): subtotal del carrito antes de
   * descuentos de pedido + lo que se está armando. La pinta <mr-tiers>.
   *
   * @param {number} total
   */
  #renderShipping(total) {
    const tiers = this.querySelector('mr-tiers');
    if (!(tiers instanceof HTMLElement)) return;
    tiers.dataset.subtotal = String((Number(this.dataset.cartTotal) || 0) + total);
  }

  /**
   * @param {number} count
   * @param {number} total
   */
  #renderBar(count, total) {
    const bar = this.querySelector('[data-mr-bar]');
    if (!(bar instanceof HTMLElement)) return;

    bar.hidden = !count;
    const countNode = bar.querySelector('[data-mr-bar-count]');
    const totalNode = bar.querySelector('[data-mr-bar-total]');
    if (countNode) countNode.textContent = `${this.querySelector('.mr-pack__summary-title')?.textContent ?? ''} (${count})`;
    if (totalNode) totalNode.textContent = this.#money(total);
  }

  /* ------------------------------------------------------------------ */
  /* Carrito                                                            */
  /* ------------------------------------------------------------------ */

  async #addToCart() {
    const lines = this.#allLines;
    const button = this.querySelector('[data-mr-add]');
    if (!lines.length || !(button instanceof HTMLButtonElement)) return;

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    this.#setStatus('');

    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: line.variantId, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    /** @type {PackLine[]} */
    const added = [];
    let sections;
    let failed = null;

    // Uno a uno: cada artículo puede llevar su propio logo, y la API del
    // carrito solo acepta archivos de a un artículo por petición.
    for (const line of lines) {
      const body = new FormData();
      body.append('id', line.variantId);
      body.append('quantity', '1');
      for (const [key, value] of line.properties) body.append(`properties[${key}]`, value);
      for (const [key, file] of line.files) body.append(`properties[${key}]`, file);
      if (this.dataset.packProperty) body.append(`properties[${this.dataset.packProperty}]`, this.dataset.packValue ?? '');
      if (line === lines[lines.length - 1] && sectionIds.length) body.append('sections', sectionIds.join(','));

      try {
        const response = await fetch(this.#route('cart_add_url', '/cart/add.js'), {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        }).then((res) => res.json());

        if (response.status) throw new Error(response.description || response.message || 'Add to cart failed');
        added.push(line);
        if (response.sections) sections = response.sections;
      } catch (error) {
        console.error(error);
        failed = line;
        break;
      }
    }

    try {
      const cart = await this.#fetchCart();
      this.dataset.cartTotal = String(cart?.items_subtotal_price ?? this.dataset.cartTotal ?? 0);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mr-pack-builder',
          itemCount: added.length,
          sections,
          didError: Boolean(failed),
        },
      });
    } catch (error) {
      deferred.reject(error);
    }

    // Lo que ya entró al carrito sale del pack; lo que falló se queda.
    for (const [step, stepLines] of this.#lines) {
      const left = stepLines.filter((line) => !added.includes(line));
      if (left.length) {
        this.#lines.set(step, left);
      } else {
        this.#lines.delete(step);
      }
    }

    button.removeAttribute('aria-busy');

    if (failed) {
      this.#setStatus((this.dataset.errorText ?? '').replace('[producto]', failed.title), 'error');
    } else {
      this.#setStatus(this.dataset.addedText ?? '');
      this.#goTo(0, { scroll: false });
    }

    this.#render();
  }

  async #fetchCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      // @ts-expect-error método propio del componente del tema
      return cartItems.fetchCartData();
    }

    const root = this.#route('cart_url', '/cart');
    return fetch(`${root}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((res) =>
      res.json()
    );
  }

  /**
   * @param {string} text
   * @param {'error' | 'info'} [tone]
   */
  #setStatus(text, tone = 'info') {
    const status = this.querySelector('[data-mr-status]');
    if (!(status instanceof HTMLElement)) return;
    status.textContent = text;
    status.dataset.tone = tone;
  }

  /**
   * Cualquier cambio del carrito (también desde el cajón) mueve la barra de
   * envío gratis, que cuenta el carrito más el pack.
   *
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    // @ts-expect-error evento estándar con la promesa del carrito
    event.promise
      ?.then((/** @type {any} */ result) => {
        const cart = result?.cart;
        // Subtotal antes de descuentos de pedido, en céntimos como los umbrales.
        // Si el evento no lo trae, se pide a /cart.js.
        const subtotal = cart?.items_subtotal_price;
        if (typeof subtotal === 'number') return subtotal;
        return fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`)
          .then((response) => response.json())
          .then((fresh) => fresh?.items_subtotal_price);
      })
      .then((/** @type {unknown} */ subtotal) => {
        if (typeof subtotal === 'number') {
          this.dataset.cartTotal = String(subtotal);
          this.#render();
        }
      })
      .catch(() => {
        /* el tema ya informa del error del carrito */
      });
  };

  /* ------------------------------------------------------------------ */
  /* Ver todos y buscador                                               */
  /* ------------------------------------------------------------------ */

  /** @param {HTMLElement} step */
  #showAll(step) {
    for (const item of step.querySelectorAll('[data-mr-item][data-extra]')) {
      if (item instanceof HTMLElement) item.hidden = false;
    }

    const more = step.querySelector('.mr-pack__more');
    if (more instanceof HTMLElement) more.dataset.open = 'true';
    step.querySelector('[data-mr-more]')?.setAttribute('hidden', '');

    const wrap = step.querySelector('[data-mr-search-wrap]');
    if (wrap instanceof HTMLElement) {
      wrap.hidden = false;
      wrap.querySelector('input')?.focus({ preventScroll: true });
    }
  }

  /** @param {string} value */
  #normalize(value) {
    return value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .trim();
  }

  /** @param {HTMLInputElement} input */
  #filter(input) {
    const step = input.closest('[data-mr-step]');
    if (!step) return;

    const query = this.#normalize(input.value);
    let visible = 0;

    for (const item of step.querySelectorAll('[data-mr-item]')) {
      if (!(item instanceof HTMLElement)) continue;
      const match = !query || this.#normalize(item.dataset.search ?? '').includes(query);
      item.hidden = !match;
      if (match) visible += 1;
    }

    const empty = step.querySelector('[data-mr-no-results]');
    if (empty instanceof HTMLElement) empty.hidden = visible > 0;
  }

  /* ------------------------------------------------------------------ */
  /* Eventos                                                            */
  /* ------------------------------------------------------------------ */

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;

    // Lo que pasa dentro de la ficha de la ventana es de la ficha.
    if (target.closest('[data-mr-dialog-body]')) return;

    const card = target.closest('[data-mr-pick]');
    if (card instanceof HTMLElement) {
      this.#pick(card);
      return;
    }

    const tab = target.closest('[data-mr-tab]');
    if (tab) {
      this.#goTo(Number(tab.getAttribute('data-mr-tab')));
      return;
    }

    const go = target.closest('[data-mr-go]');
    if (go) {
      this.#goTo(Number(go.getAttribute('data-mr-go')));
      return;
    }

    if (target.closest('[data-mr-skip], [data-mr-next]')) {
      this.#advance();
      return;
    }

    if (target.closest('[data-mr-prev]')) {
      this.#goTo(this.#current - 1);
      return;
    }

    const more = target.closest('[data-mr-more]');
    if (more) {
      const step = more.closest('[data-mr-step]');
      if (step instanceof HTMLElement) this.#showAll(step);
      return;
    }

    const remove = target.closest('[data-mr-remove]');
    if (remove instanceof HTMLElement) {
      this.#remove(Number(remove.dataset.step), remove.dataset.mrRemove ?? '');
      return;
    }

    const change = target.closest('[data-mr-change]');
    if (change instanceof HTMLElement) {
      const step = Number(change.dataset.step);
      const line = (this.#lines.get(step) ?? []).find((item) => item.key === change.dataset.mrChange);
      const lineCard = line ? this.#cardFor(step, line.productId) : null;
      if (line && lineCard) this.#openOptions(step, lineCard, line.key);
      return;
    }

    if (target.closest('[data-mr-close]')) {
      this.#closeDialog();
      return;
    }

    if (target.closest('[data-mr-add]')) {
      this.#addToCart();
      return;
    }

    if (target.closest('[data-mr-to-summary]')) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.querySelector('[data-mr-summary]')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    }
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-mr-search]')) this.#filter(event.target);
  };

  /**
   * Rutas del tema (con el prefijo de idioma si lo hay). `Theme` es una
   * constante global de snippets/scripts.liquid, no una propiedad de window.
   *
   * @param {string} name
   * @param {string} fallback
   */
  #route(name, fallback) {
    // @ts-expect-error global del tema
    const routes = typeof Theme === 'undefined' ? null : Theme.routes;
    return routes?.[name] ?? fallback;
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat ?? '{{amount}}';
    const withComma = format.includes('amount_with_comma_separator');
    const number = (cents / 100).toLocaleString(withComma ? 'es-ES' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    return format.replace(/\{\{\s*amount[^}]*\}\}/, number);
  }
}

if (!customElements.get('mr-pack-builder')) {
  customElements.define('mr-pack-builder', MrPackBuilder);
}
